import React, { CSSProperties, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { List, useListRef } from 'react-window';
import { TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import {
    CardHandle,
    ExpansionPanel,
    ExpansionPanelActionEvent,
    ExpansionPanelContent,
} from '@progress/kendo-react-layout';
import cn from 'clsx';
import {
    CategoryType,
    FilterCategory,
    FilterCategoryItems,
    StudyDataFilterHandle,
    StudyFilterProps,
} from './study-data-filter.interfaces';
import styles from './study-data-filter.module.scss';
import Button from '@components-clean/core/button';
import Card from '@components-clean/core/card';
import CoreCheckbox from '@components-clean/core/core-checkbox';
import { InfoField } from '@components-clean/core/info-field';
import InteractiveList from '@components-clean/core/interactive-list';
import Loading from '@components-clean/core/loading/loading';
import { SearchBox } from '@components-clean/core/search-box';
import { AdvancedSelect } from '@components-clean/core/study-data-filter/advanced-select/advanced-select';
import { AdvanceSelectFilter } from '@components-clean/core/study-data-filter/advanced-select/advanced-select.interface';
import Tooltip from '@components-clean/core/tooltip';
import {
    getEmitFilterChangedEvent,
    getFilterHttpRequestQueue,
    useStudyDataFilterContext,
} from '@contexts-clean/study-data-filter-context';
import { useOnclickOutside } from '@hooks-clean/use-onclick-outside';
import { FilterUIValue, FilterUIAction, FilterUIStateUpdate, FilterUIExclusion } from '@interfaces/central-monitoring';
import { PopupService } from '@utils-clean/popup-service';
import * as utils from '@utils-clean/study-data-filter/study-data-filter.service';
import {
    getExpansionPanelTitle,
    MapCalculateFiltersToChips,
} from '@utils-clean/study-data-filter/study-data-filter.service';
import {
    useApplyFilterUIValues,
    useGetFilterUIValues,
    usePerformFilterUIAction,
} from '@apis/bff-api/central-monitoring';

const CHECKBOX_SIZE = 23;
const CHECKBOX_ROW_PADDING = 5;
const ROW_SIZE = CHECKBOX_SIZE + CHECKBOX_ROW_PADDING;
const LIST_HEIGHT = 320;

// TODO: refactor to use ref as a prop - forwardRef was deprecated in React 19
// Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
// eslint-disable-next-line @eslint-react/no-forward-ref
export const StudyDataFilter = forwardRef<StudyDataFilterHandle, StudyFilterProps>(
    (
        {
            categories,
            datasetId,
            'data-testid': testid,
            studyRef,
            onApply,
            dataViewRef,
            defaultCategory,
            onClose,
            filterChipElements,
        },
        ref,
    ) => {
        const [filterCategories, setFilterCategories] = useState<Array<FilterCategory>>(categories);
        const [selectedCategory, setSelectedCategory] = useState<FilterCategoryItems | undefined>();
        const [categoryFilter, setCategoryFilter] = useState<string>('');
        const [itemFilter, setItemFilter] = useState<string>('');
        const [checkboxes, setCheckboxes] = useState<Array<FilterUIValue>>([]);
        const [expanded, setExpanded] = useState<CategoryType | undefined>();
        const [showAdvancedSelect, setShowAdvancedSelect] = useState<boolean>(false);
        const [filterValues, setFilterValues] = useState<Array<FilterUIValue>>([]);
        const [trackedChanges, setTrackedChanges] = useState<FilterUIStateUpdate>({
            context: undefined,
            fieldKey: undefined,
            checkedValueIds: [],
            uncheckedValueIds: [],
        });
        const [showLoading, setShowLoading] = useState<boolean>(false);

        const listRef = useListRef(null);
        const advancedSelectAnchorRef = useRef<HTMLDivElement | null>(null);
        const cardRef = useRef<CardHandle>(null);
        const advancedSelectRef = useRef<HTMLDivElement>(null);

        const [isStale, setIsStale] = useState<boolean>(false);
        const { sessionId, setIsFiltered, setChipData } = useStudyDataFilterContext();
        const { emitFilterChangedEvent } = getEmitFilterChangedEvent();
        const httpRequestQueue$ = getFilterHttpRequestQueue();

        const {
            data: filterValueData,
            trigger: getFilterValues,
            error: getValuesError,
        } = useGetFilterUIValues(
            studyRef,
            sessionId,
            datasetId,
            selectedCategory?.context,
            selectedCategory?.fieldKey,
            dataViewRef,
        );

        const { trigger: performFilterValueAction } = usePerformFilterUIAction(
            studyRef,
            sessionId,
            datasetId,
            dataViewRef,
        );

        const { trigger: applyFilterUIValues } = useApplyFilterUIValues(studyRef, sessionId, datasetId, dataViewRef);

        useEffect(() => {
            if (!filterValueData) return;
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setFilterValues(filterValueData);
        }, [filterValueData]);

        const { t }: { t: (key: string) => string } = useTranslation();

        const changeSelectedCategory = (parentCategory: CategoryType, categoryId: number) => {
            if (isStale) return;
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            /* eslint-disable @eslint-react/set-state-in-effect */
            setExpanded(parentCategory);
            setSelectedCategory(
                categories?.find((c) => c.id === parentCategory)?.items.find((i) => i.id === categoryId),
            );
            /* eslint-enable @eslint-react/set-state-in-effect */
        };

        useImperativeHandle(ref, () => ({
            setSelectedCategory: changeSelectedCategory,
        }));

        useEffect(() => {
            const items = categories
                .map((item) => {
                    return {
                        id: item.id,
                        items:
                            item.items.filter(
                                (x) => x.label.toLowerCase().indexOf(categoryFilter.toLowerCase()) !== -1,
                            ) ?? [],
                    };
                })
                .filter((x) => x.items.length > 0);
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            /* eslint-disable @eslint-react/set-state-in-effect */
            setFilterCategories(items);

            if (items.length > 0 && categoryFilter.length > 0) {
                setExpanded(items[0].id);
            }
            /* eslint-enable @eslint-react/set-state-in-effect */
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/exhaustive-deps
        }, [categoryFilter]);

        useEffect(() => {
            if (defaultCategory) changeSelectedCategory(defaultCategory.parentCategory, defaultCategory.categoryId);
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/exhaustive-deps
        }, [defaultCategory]);

        useEffect(() => {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setCheckboxes(filterValues.filter((x) => x.label.toLowerCase().indexOf(itemFilter.toLowerCase()) !== -1));
        }, [itemFilter, filterValues]);

        useEffect(() => {
            if (selectedCategory?.id != null) {
                // We want to show the loader while the request sits in the queue
                // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
                // eslint-disable-next-line @eslint-react/set-state-in-effect
                setShowLoading(true);
                httpRequestQueue$.next(() =>
                    getFilterValues(trackedChanges)
                        .then(() => {
                            setTrackedChanges({
                                context: selectedCategory.context,
                                fieldKey: selectedCategory.fieldKey,
                                checkedValueIds: [],
                                uncheckedValueIds: [],
                            });
                        })
                        .catch((err) => {
                            window.console.error(err);
                            setCheckboxes([]);
                            setFilterValues([]);
                        })
                        .finally(() => setShowLoading(false)),
                );
            }
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/exhaustive-deps
        }, [selectedCategory]);

        const closeStudyFilter = () => {
            httpRequestQueue$.next(() =>
                performFilterValueAction({ action: FilterUIAction.ClearUnappliedChanges }).catch((err) =>
                    window.console.error(err),
                ),
            );
            onClose();
        };

        useOnclickOutside(
            cardRef.current?.element,
            () => {
                closeStudyFilter();
            },
            (target: Node) => {
                const chipOrAdvancedSelect =
                    filterChipElements?.current
                        ?.values()
                        .toArray()
                        .some((x) => x.contains(target)) || advancedSelectRef.current?.contains(target);
                if (chipOrAdvancedSelect) return chipOrAdvancedSelect;

                // Now look for any element within the `filter-ui-excluded` elements
                // This should be dropdowns within the Advanced Select, but they're created
                // at the document level. We can't use the appendTo either due to clipping
                const excludedDropdowns = document.querySelector('.filter-ui-excluded');
                if (excludedDropdowns) return excludedDropdowns.contains(target);

                return false;
            },
        );

        useEffect(() => {
            const subscription = PopupService.closePopupsObservable().subscribe(() => closeStudyFilter());

            return () => {
                subscription.unsubscribe();
            };
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/exhaustive-deps
        }, []);

        const selectFilterValues = (checked: boolean) => {
            if (!selectedCategory) return;

            // If there are no checkboxes that aren't excluded. Do nothing
            if (!checkboxes.find((x) => x.exclusion !== FilterUIExclusion.Value)) return;

            const changed: number[] = [];
            setCheckboxes((prevState) =>
                prevState.map((x) => {
                    if (x.exclusion !== FilterUIExclusion.Value && x.checked !== checked) {
                        x.checked = checked;
                        changed.push(x.id);
                    }

                    return x;
                }),
            );

            // A filtered (un)select all will remain as tracked changes and passed on the next Modify request
            if (checkboxes.length !== filterValues.length) {
                if (checked) {
                    setTrackedChanges({
                        ...trackedChanges,
                        checkedValueIds: trackedChanges.checkedValueIds.concat(changed),
                        uncheckedValueIds: trackedChanges.uncheckedValueIds.filter((x) => changed.indexOf(x) === -1),
                    });
                } else {
                    setTrackedChanges({
                        ...trackedChanges,
                        checkedValueIds: trackedChanges.checkedValueIds.filter((x) => changed.indexOf(x) === -1),
                        uncheckedValueIds: trackedChanges.uncheckedValueIds.concat(changed),
                    });
                }

                return;
            }

            // Do this before the request instead of after. If users toggle checkbox values while the http request is ongoing
            // we don't want to wipe out those changes
            setTrackedChanges({
                ...trackedChanges,
                checkedValueIds: [],
                uncheckedValueIds: [],
            });

            httpRequestQueue$.next(() =>
                performFilterValueAction({
                    action: checked ? FilterUIAction.SelectAll : FilterUIAction.UnselectAll,
                    context: selectedCategory.context,
                    fieldKey: selectedCategory.fieldKey,
                }).catch((err) => window.console.error(err)),
            );
        };

        const onCheckboxChange = (value: boolean, filterValue: unknown, valueId: number) => {
            if (!selectedCategory) return;
            setCheckboxes((prevState) =>
                prevState.map((x) => {
                    if (x.value === filterValue) {
                        x.checked = value;
                    }
                    return x;
                }),
            );

            const changes: FilterUIStateUpdate = trackedChanges;
            if (value) {
                const index = trackedChanges.uncheckedValueIds.indexOf(valueId);
                if (index > -1) {
                    changes.uncheckedValueIds.splice(index, 1);
                } else {
                    changes.checkedValueIds.push(valueId);
                }
            } else {
                const index = trackedChanges.checkedValueIds.indexOf(valueId);
                if (index > -1) {
                    changes.checkedValueIds.splice(index, 1);
                } else {
                    changes.uncheckedValueIds.push(valueId);
                }
            }

            setTrackedChanges(changes);
        };

        const onClearClick = () => {
            // Do this before the request instead of after. If users toggle checkbox values while the http request is ongoing
            // we don't want to wipe out those changes
            setTrackedChanges({
                ...trackedChanges,
                checkedValueIds: [],
                uncheckedValueIds: [],
            });
            httpRequestQueue$.next(() =>
                performFilterValueAction({ action: FilterUIAction.Clear })
                    .then((response) => {
                        setIsStale(response);
                        if (response) setCheckboxes([]);
                    })
                    .catch((err) => window.console.error(err)),
            );

            if (selectedCategory) {
                // This isn't great, we want to somehow trigger the virtual list to re-render
                setCheckboxes((prevState) =>
                    prevState.map((x) => {
                        x.checked = true;
                        if (x.exclusion !== FilterUIExclusion.Dataset) x.exclusion = FilterUIExclusion.None;
                        return x;
                    }),
                );
            }
        };

        const onApplyClick = () => {
            // Now that it's in a queue, does there need to be some feedback to the user?
            setShowLoading(true);
            httpRequestQueue$.next(() =>
                applyFilterUIValues(trackedChanges)
                    .then((resp) => {
                        setTrackedChanges({
                            context: selectedCategory?.context,
                            fieldKey: selectedCategory?.fieldKey,
                            checkedValueIds: [],
                            uncheckedValueIds: [],
                        });
                        setIsFiltered((resp.filteredFields?.length ?? 0) > 0);
                        setIsStale(resp.isStale ?? false);

                        // If stale, then no filters were applied. No need to emit events or perform onApply
                        // Don't wipe out any existing chip data
                        if (!resp.isStale) {
                            setChipData(MapCalculateFiltersToChips(resp, categories));
                            emitFilterChangedEvent();
                            if (onApply) onApply();
                        } else {
                            setCheckboxes([]);
                        }
                    })
                    .finally(() => setShowLoading(false))
                    .catch((err) => window.console.error(err)),
            );
        };

        const onCategorySelect = (id: number) => {
            const cat = categories.flatMap((c) => c.items).find((c) => c.id === id);

            if (selectedCategory === undefined) {
                setTrackedChanges({
                    context: cat?.context,
                    fieldKey: cat?.fieldKey,
                    checkedValueIds: [],
                    uncheckedValueIds: [],
                });
            }

            setSelectedCategory(cat);
            setShowAdvancedSelect(false);
            // Maybe put this check in a use effect
            if (listRef?.current)
                listRef.current.scrollToRow({
                    index: 0,
                });
        };

        const onAdvancedSelectClick = () => {
            setShowAdvancedSelect(!showAdvancedSelect);
        };

        const onAdvancedSelectApply = (data: AdvanceSelectFilter) => {
            if (!selectedCategory) return;

            if (data.filters.length > 0) {
                const checked: Array<number> = [];
                const unchecked: Array<number> = [];
                // Apply the select across an unfiltered group
                filterValues.forEach((x) => {
                    if (x.exclusion !== FilterUIExclusion.Value) {
                        const matchesFilter = utils.ItemMatchesFilter(x, data);
                        if (matchesFilter && !x.checked) {
                            x.checked = true;
                            checked.push(x.id);
                        } else if (!matchesFilter && x.checked) {
                            x.checked = false;
                            unchecked.push(x.id);
                        }
                    }
                });

                // Update tracked changes, only adding new changes
                setTrackedChanges({
                    ...trackedChanges,
                    checkedValueIds: trackedChanges.checkedValueIds
                        .filter((u) => unchecked.indexOf(u) === -1)
                        .concat(checked.filter((u) => trackedChanges.uncheckedValueIds.indexOf(u) === -1)),
                    uncheckedValueIds: trackedChanges.uncheckedValueIds
                        .filter((u) => checked.indexOf(u) === -1)
                        .concat(unchecked.filter((u) => trackedChanges.checkedValueIds.indexOf(u) === -1)),
                });

                // Re-apply any item filter
                if (itemFilter) {
                    setCheckboxes(
                        filterValues.filter((x) => x.label.toLowerCase().indexOf(itemFilter.toLowerCase()) !== -1),
                    );
                } else {
                    setCheckboxes(filterValues);
                }
            }

            setShowAdvancedSelect(false);
        };

        type RowProps = { index?: number; style?: CSSProperties };

        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/no-nested-component-definitions
        const CheckboxRow = ({ index, style }: RowProps) => {
            if (!selectedCategory || index == null) return null;
            const x = checkboxes[index];
            const datasetExcluded = x.exclusion === FilterUIExclusion.Dataset;

            return (
                <div style={{ ...style, padding: `${CHECKBOX_ROW_PADDING}px` }}>
                    <Tooltip position="top" parentTitle>
                        <span title={x.label} className={styles.opraScreenFilterCheckbox}>
                            <CoreCheckbox
                                data-testid={`checkbox-${index}`}
                                label={x.label}
                                value={x.checked}
                                key={index}
                                onChange={(e) => onCheckboxChange(e.value, x.value, x.id)}
                                disabled={x.exclusion === FilterUIExclusion.Value}
                                labelClassName="k-overflow-hidden k-text-ellipsis"
                                defaultValue
                                alternativeStyle={datasetExcluded}
                            />
                        </span>
                    </Tooltip>
                </div>
            );
        };

        const emptyStateLeftSide = (
            <InfoField message={t('cm:componentsClean.screenFilter.noDataFound')} type="Info" layout="center" />
        );

        const emptyStateRightSide = (
            <InfoField
                message={
                    selectedCategory
                        ? !checkboxes.length && !itemFilter.length
                            ? t('cm:componentsClean.screenFilter.noValuesForDataset')
                            : t('cm:componentsClean.screenFilter.noDataFound')
                        : t('screenFilterNotPicked')
                }
                type="Info"
                layout="center"
            />
        );

        const errorStateRightSide = (
            <InfoField message={t('cm:componentsClean.screenFilter.errorLoadingValues')} type="Error" layout="center" />
        );

        return (
            <Card
                ref={cardRef}
                data-testid={testid}
                title={t('filterCategories')}
                className={styles.cardWrapper}
                padding="none"
                headerActions={
                    <Button primary data-testid="close-button" onClick={closeStudyFilter}>
                        {t('close')}
                    </Button>
                }
                footerActions={
                    <div className={styles.footerActionsWrapper}>
                        <Button data-testid="clear-button" onClick={onClearClick} disabled={isStale}>
                            {t('reset')}
                        </Button>
                        <Button data-testid="apply-button" primary onClick={onApplyClick} disabled={isStale}>
                            {t('apply')}
                        </Button>
                    </div>
                }
            >
                {isStale && (
                    <InfoField
                        message={t('cm:componentsClean.screenFilter.filterSessionStale')}
                        type="Warning"
                        layout="cardOverlay"
                    />
                )}
                <div className={styles.contentWrapper}>
                    <div className={styles.leftContent}>
                        <SearchBox
                            data-testid="search-box-left"
                            fillMode="flat"
                            placeholder={t('cm2EnterValue')}
                            showSearchIcon
                            value={categoryFilter}
                            onChange={(e: TextBoxChangeEvent) => {
                                if (e.value as string) {
                                    setCategoryFilter(e.value as string);
                                } else {
                                    setCategoryFilter(e.target.value as string);
                                }
                            }}
                            style={{ width: '100%' }}
                            debounceTime={200}
                            disabled={isStale}
                        />
                        <div className={cn(styles.categoryPanels, 'k-flex-col', 'k-gap-1')}>
                            {filterCategories.length === 0 && emptyStateLeftSide}
                            {!isStale &&
                                filterCategories.map((c) => {
                                    return (
                                        <ExpansionPanel
                                            title={getExpansionPanelTitle(c.id)}
                                            expanded={expanded === c.id}
                                            key={c.id}
                                            className="opra-expansion-panel"
                                            onAction={(event: ExpansionPanelActionEvent) => {
                                                setExpanded(event.expanded ? undefined : c.id);
                                            }}
                                        >
                                            {expanded === c.id && (
                                                <ExpansionPanelContent
                                                    onKeyDown={(event) => {
                                                        // Stop the Enter or Space key from propagating
                                                        // Otherwise the expansion panel will close, instead of
                                                        // interacting with the child elements
                                                        if (event.code === 'Enter' || event.code === 'Space') {
                                                            event.stopPropagation();
                                                        }
                                                    }}
                                                    className={styles.expansionPanelContent}
                                                >
                                                    <InteractiveList
                                                        data={c.items.sort((a, b) => a.index - b.index)}
                                                        onSelect={onCategorySelect}
                                                        selectedId={selectedCategory?.id}
                                                    />
                                                </ExpansionPanelContent>
                                            )}
                                        </ExpansionPanel>
                                    );
                                })}
                        </div>
                    </div>
                    <div className={styles.rightContent}>
                        <SearchBox
                            data-testid="search-box-right"
                            fillMode="flat"
                            placeholder={t('cm2EnterValue')}
                            showSearchIcon
                            value={itemFilter}
                            onChange={(e: TextBoxChangeEvent) => {
                                if (e.value as string) {
                                    setItemFilter(e.value as string);
                                } else {
                                    setItemFilter(e.target.value as string);
                                }
                            }}
                            style={{ width: '100%' }}
                            debounceTime={200}
                            disabled={isStale}
                        />
                        <div className={styles.filterButtonsWrapper} ref={advancedSelectAnchorRef}>
                            <Button
                                data-testid="unselect-all"
                                onClick={() => selectFilterValues(false)}
                                disabled={!checkboxes.length}
                            >
                                {t('unselectAll')}
                            </Button>
                            <Button
                                data-testid="select-all"
                                onClick={() => selectFilterValues(true)}
                                disabled={!checkboxes.length}
                            >
                                {t('selectAll')}
                            </Button>
                            <Button
                                data-testid="advanced-select"
                                primary
                                onClick={onAdvancedSelectClick}
                                disabled={!checkboxes.length}
                            >
                                {t('advancedSelect')}
                            </Button>
                        </div>
                        <div className={styles.checkboxListWrapper}>
                            {showLoading && <Loading />}
                            {!showLoading &&
                                !isStale &&
                                (checkboxes.length === 0 || !selectedCategory) &&
                                (getValuesError ? errorStateRightSide : emptyStateRightSide)}
                            {!showLoading && checkboxes.length > 0 && (
                                <List
                                    className={styles.autoSizeWillChange}
                                    rowCount={checkboxes.length}
                                    rowHeight={ROW_SIZE}
                                    listRef={listRef}
                                    rowProps={{}}
                                    rowComponent={CheckboxRow}
                                    style={{ height: LIST_HEIGHT }}
                                />
                            )}
                        </div>
                    </div>
                </div>
                {selectedCategory && (
                    <AdvancedSelect
                        ref={advancedSelectRef}
                        key={selectedCategory.id}
                        anchor={advancedSelectAnchorRef.current}
                        dataType={selectedCategory.dataType}
                        show={showAdvancedSelect}
                        anchorAlign={{ vertical: 'top', horizontal: 'right' }}
                        popupAlign={{ vertical: 'top', horizontal: 'left' }}
                        onCancel={() => setShowAdvancedSelect(false)}
                        onSelect={onAdvancedSelectApply}
                    />
                )}
            </Card>
        );
    },
);
